import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  WhatsApp as WhatsAppIcon,
  Group as GroupIcon,
  DirectionsCar as CarIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leadService } from '../services/leadService';
import { Lead, LeadFilters } from '../types';
import { formatPhoneNumber } from '../utils/phoneUtils';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'error'> = {
  new: 'primary',
  contacted: 'default',
  converted: 'success',
  lost: 'error',
};

const SOURCE_LABELS: Record<string, string> = {
  whatsapp_sender: 'WhatsApp Sender',
  ai_extracted: 'AI Extracted',
  manual: 'Manual',
};

const Leads: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<LeadFilters>({ page: 1, limit: 20 });
  const [searchInput, setSearchInput] = useState('');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [msgLead, setMsgLead] = useState<Lead | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['leads', filters],
    queryFn: () => leadService.getLeads(filters),
  });

  const { data: leadDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['lead-messages', msgLead?._id],
    queryFn: () => leadService.getLead(msgLead!._id),
    enabled: Boolean(msgLead),
  });

  const { data: stats } = useQuery({
    queryKey: ['lead-stats'],
    queryFn: () => leadService.getStats(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { status?: string; notes?: string } }) =>
      leadService.updateLead(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead-stats'] });
      toast.success('Lead updated');
      setEditDialogOpen(false);
    },
    onError: () => toast.error('Failed to update lead'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => leadService.deleteLead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead-stats'] });
      toast.success('Lead deleted');
    },
    onError: () => toast.error('Failed to delete lead'),
  });

  const handleSearch = () => {
    setFilters((f) => ({ ...f, page: 1, search: searchInput || undefined }));
  };

  const handleStatusFilter = (status: string) => {
    setFilters((f) => ({ ...f, page: 1, status: status || undefined }));
  };

  const openEdit = (lead: Lead) => {
    setSelectedLead(lead);
    setEditStatus(lead.status);
    setEditNotes(lead.notes || '');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!selectedLead) return;
    updateMutation.mutate({ id: selectedLead._id, updates: { status: editStatus, notes: editNotes } });
  };

  const handleDelete = (lead: Lead) => {
    if (!window.confirm(`Delete lead for ${lead.phone}?`)) return;
    deleteMutation.mutate(lead._id);
  };

  const displayName = (lead: Lead) =>
    lead.name || lead.senderName || '—';

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  if (isLoading) return <LoadingSpinner />;

  return (
    <Box sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Leads
        </Typography>
        <Tooltip title="Refresh">
          <IconButton
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['leads'] });
              queryClient.invalidateQueries({ queryKey: ['lead-stats'] });
            }}
            disabled={isFetching}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats row */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total', value: stats.total, color: '#1976d2' },
            { label: 'New', value: stats.new, color: '#1976d2' },
            { label: 'Contacted', value: stats.contacted, color: '#757575' },
            { label: 'Converted', value: stats.converted, color: '#2e7d32' },
            { label: 'Lost', value: stats.lost, color: '#d32f2f' },
          ].map((s) => (
            <Grid item xs={6} sm={4} md={2.4} key={s.label}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="h5" fontWeight={700} color={s.color}>
                    {s.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {s.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search phone, name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
        <Button variant="contained" size="small" onClick={handleSearch}>
          Search
        </Button>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={filters.status || ''}
            onChange={(e) => handleStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="new">New</MenuItem>
            <MenuItem value="contacted">Contacted</MenuItem>
            <MenuItem value="converted">Converted</MenuItem>
            <MenuItem value="lost">Lost</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell><strong>Phone</strong></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Source</strong></TableCell>
              <TableCell><strong>Group(s)</strong></TableCell>
              <TableCell align="center"><strong>Rides</strong></TableCell>
              <TableCell><strong>First Seen</strong></TableCell>
              <TableCell><strong>Last Seen</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Last Message</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.leads?.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No leads found</Typography>
                </TableCell>
              </TableRow>
            )}
            {data?.leads.map((lead) => (
              <TableRow key={lead._id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon fontSize="inherit" color="action" />
                    <Typography variant="body2" fontFamily="monospace">
                      {formatPhoneNumber(lead.phone)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon fontSize="inherit" color="action" />
                    <Typography variant="body2">{displayName(lead)}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={SOURCE_LABELS[lead.source] ?? lead.source}
                    size="small"
                    variant="outlined"
                    icon={lead.source === 'whatsapp_sender' ? <WhatsAppIcon /> : undefined}
                  />
                </TableCell>
                <TableCell>
                  {lead.groups.length > 0 ? (
                    <Tooltip title={lead.groups.join(', ')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <GroupIcon fontSize="inherit" color="action" />
                        <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                          {lead.groups[0]}
                          {lead.groups.length > 1 && ` +${lead.groups.length - 1}`}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <CarIcon fontSize="inherit" color="action" />
                    <Typography variant="body2">{lead.rideCount}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(lead.firstSeen)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(lead.lastSeen)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={lead.status}
                    size="small"
                    color={STATUS_COLORS[lead.status] ?? 'default'}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 220 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={lead.lastMessage || ''}>
                      <Typography variant="body2" noWrap color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                        {lead.lastMessage || '—'}
                      </Typography>
                    </Tooltip>
                    <Tooltip title="View messages">
                      <IconButton size="small" onClick={() => setMsgLead(lead)}>
                        <ChatIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(lead)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(lead)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination footer */}
      {data && data.pagination.total > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {(() => {
              const page = filters.page ?? 1;
              const limit = filters.limit ?? 20;
              const start = (page - 1) * limit + 1;
              const end = Math.min(page * limit, data.pagination.total);
              return `Showing ${start}–${end} of ${data.pagination.total} leads`;
            })()}
          </Typography>

          {data.pagination.pages > 1 && (
            <Pagination
              count={data.pagination.pages}
              page={filters.page ?? 1}
              onChange={(_, page) => setFilters((f) => ({ ...f, page }))}
              color="primary"
              size="small"
            />
          )}
        </Box>
      )}

      {/* Messages Dialog */}
      <Dialog open={Boolean(msgLead)} onClose={() => setMsgLead(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Messages — {msgLead && formatPhoneNumber(msgLead.phone)}
          {(msgLead?.name || msgLead?.senderName)
            ? ` · ${msgLead.name || msgLead.senderName}`
            : ''}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : (
            <Stack spacing={2}>
              {msgLead?.lastMessage && (
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Last Message</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {msgLead.lastMessage}
                  </Typography>
                </Paper>
              )}
              {!leadDetail?.rideIds?.length ? (
                <Typography variant="body2" color="text.secondary">No ride messages found for this lead.</Typography>
              ) : (
                [...(leadDetail.rideIds ?? [])]
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((ride) => (
                    <Paper key={ride._id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" mb={1} alignItems="center">
                        {ride.aiAnalysis?.from && ride.aiAnalysis?.to && (
                          <Chip size="small" label={`${ride.aiAnalysis.from} → ${ride.aiAnalysis.to}`} />
                        )}
                        <Chip size="small" label={ride.status} variant="outlined" />
                        {ride.messageInfo?.groupName && (
                          <Chip size="small" icon={<GroupIcon />} label={ride.messageInfo.groupName} variant="outlined" />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {new Date(ride.createdAt).toLocaleString('en-IN')}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}
                      >
                        {ride.originalMessage || '—'}
                      </Typography>
                    </Paper>
                  ))
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMsgLead(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Lead — {selectedLead && formatPhoneNumber(selectedLead.phone)}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="contacted">Contacted</MenuItem>
              <MenuItem value="converted">Converted</MenuItem>
              <MenuItem value="lost">Lost</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Notes"
            multiline
            rows={3}
            fullWidth
            size="small"
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={updateMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Leads;
